import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  RefreshCw, 
  Pause,
  Play,
  XCircle,
  Server,
  PhoneOff,
  TrendingUp,
  Calendar,
  ChevronDown,
  ChevronUp,
  Eye
} from 'lucide-react';
import { campaignsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const CampaignHealthDashboard = () => {
  const { clientUser } = useAuth();
  const [healthData, setHealthData] = useState(null);
  const [resumeHistory, setResumeHistory] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedCampaigns, setExpandedCampaigns] = useState({});

  const clientId = clientUser?.id;

  const fetchHealthData = async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const response = await campaignsAPI.getCampaignHealth(clientId);
      setHealthData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch health data:', err);
      setError('Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  const fetchResumeHistory = async () => {
    if (!clientId) return;
    try {
      const response = await campaignsAPI.getResumeHistory(clientId, 30);
      setResumeHistory(response.data);
    } catch (err) {
      console.error('Failed to fetch resume history:', err);
    }
  };

  const fetchCampaignDetail = async (campaignId) => {
    if (!clientId) return;
    try {
      const response = await campaignsAPI.getCampaignHealthDetail(clientId, campaignId);
      setCampaignDetail(response.data);
      setSelectedCampaign(campaignId);
    } catch (err) {
      console.error('Failed to fetch campaign detail:', err);
    }
  };

  useEffect(() => {
    fetchHealthData();
    fetchResumeHistory();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealthData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getHealthStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'completed': return 'text-blue-400';
      case 'paused': return 'text-orange-400';
      default: return 'text-gray-400';
    }
  };

  const getHealthStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'completed': return <CheckCircle className="w-5 h-5 text-blue-400" />;
      case 'paused': return <Pause className="w-5 h-5 text-orange-400" />;
      default: return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      running: 'bg-green-500/20 text-green-400 border-green-500/30',
      paused: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      completed: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      draft: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      scheduled: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
    };
    return colors[status] || colors.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const toggleExpand = (campaignId) => {
    setExpandedCampaigns(prev => ({
      ...prev,
      [campaignId]: !prev[campaignId]
    }));
  };

  if (loading && !healthData) {
    return (
      <div className="min-h-screen bg-gray-900 p-6 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin" />
          <span>Loading health data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Activity className="w-7 h-7 text-purple-400" />
              Campaign Health Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Monitor campaign status, restarts, and failed attempts</p>
          </div>
          <button
            onClick={fetchHealthData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6 text-red-400">
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'resume-history', 'campaigns'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === tab 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'resume-history' && 'Resume History'}
              {tab === 'campaigns' && 'All Campaigns'}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && healthData && (
          <div className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                <div className="flex items-center gap-2 text-gray-400 text-sm mb-2">
                  <Server className="w-4 h-4" />
                  Total Campaigns
                </div>
                <div className="text-2xl font-bold text-white">{healthData.summary.total_campaigns}</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-green-500/30">
                <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                  <Play className="w-4 h-4" />
                  Running
                </div>
                <div className="text-2xl font-bold text-green-400">{healthData.summary.running}</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-orange-500/30">
                <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
                  <Pause className="w-4 h-4" />
                  Paused
                </div>
                <div className="text-2xl font-bold text-orange-400">{healthData.summary.paused}</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-blue-500/30">
                <div className="flex items-center gap-2 text-blue-400 text-sm mb-2">
                  <CheckCircle className="w-4 h-4" />
                  Completed
                </div>
                <div className="text-2xl font-bold text-blue-400">{healthData.summary.completed}</div>
              </div>
              <div className="bg-gray-800 rounded-xl p-4 border border-purple-500/30">
                <div className="flex items-center gap-2 text-purple-400 text-sm mb-2">
                  <RefreshCw className="w-4 h-4" />
                  Resumes (7d)
                </div>
                <div className="text-2xl font-bold text-purple-400">{healthData.summary.total_resumes_7d}</div>
              </div>
            </div>

            {/* Recent Resume Events */}
            {healthData.recent_resumes && healthData.recent_resumes.length > 0 && (
              <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-purple-400" />
                  <h2 className="text-lg font-semibold text-white">Recent Resume Events</h2>
                </div>
                <div className="divide-y divide-gray-700">
                  {healthData.recent_resumes.map((event, idx) => (
                    <div key={`resume-${event.campaign_id || event.campaign_name}-${event.timestamp || idx}`} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <div className="text-white font-medium">{event.campaign_name}</div>
                        <div className="text-gray-400 text-sm">{event.contacts_remaining} contacts remaining</div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-400 text-sm">{formatDate(event.timestamp)}</div>
                        <div className="text-purple-400 text-xs">Server Restart</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Campaign Health List */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <h2 className="text-lg font-semibold text-white">Campaign Health Status</h2>
              </div>
              <div className="divide-y divide-gray-700">
                {healthData.campaigns.map(campaign => (
                  <div key={campaign.campaign_id} className="px-4 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getHealthStatusIcon(campaign.health_status)}
                        <div>
                          <div className="text-white font-medium">{campaign.name}</div>
                          <div className="text-gray-400 text-sm">{campaign.agent_name}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(campaign.status)}`}>
                          {campaign.status.toUpperCase()}
                        </span>
                        <div className="text-right">
                          <div className="text-white">{Math.min(100, campaign.progress_percent)}%</div>
                          <div className="text-gray-400 text-xs">{campaign.completed}/{campaign.total_contacts}</div>
                        </div>
                        <button
                          onClick={() => fetchCampaignDetail(campaign.campaign_id)}
                          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Progress bar */}
                    <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-purple-600 to-purple-400 transition-all duration-500"
                        style={{ width: `${Math.min(100, campaign.progress_percent)}%` }}
                      />
                    </div>

                    {/* Failure warning */}
                    {campaign.recent_failures_24h > 0 && (
                      <div className="mt-2 flex items-center gap-2 text-yellow-400 text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        {campaign.recent_failures_24h} failed attempts in last 24h
                      </div>
                    )}
                  </div>
                ))}

                {healthData.campaigns.length === 0 && (
                  <div className="px-4 py-8 text-center text-gray-400">
                    No campaigns found
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Resume History Tab */}
        {activeTab === 'resume-history' && resumeHistory && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-purple-400" />
                <h2 className="text-lg font-semibold text-white">Resume History (Last 30 Days)</h2>
              </div>
              <div className="text-gray-400 text-sm">{resumeHistory.total_resumes} total resumes</div>
            </div>
            
            {Object.keys(resumeHistory.by_date || {}).length > 0 ? (
              <div className="divide-y divide-gray-700">
                {Object.entries(resumeHistory.by_date).map(([date, events]) => (
                  <div key={date} className="px-4 py-3">
                    <div 
                      className="flex items-center justify-between cursor-pointer"
                      onClick={() => toggleExpand(date)}
                    >
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                        <span className="text-white font-medium">{new Date(date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                        <span className="text-gray-400 text-sm">({events.length} resume{events.length !== 1 ? 's' : ''})</span>
                      </div>
                      {expandedCampaigns[date] ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                    </div>
                    
                    {expandedCampaigns[date] && (
                      <div className="mt-3 ml-4 space-y-2">
                        {events.map((event, idx) => (
                          <div key={`event-${event.campaign_id || event.campaign_name}-${event.timestamp || idx}`} className="bg-gray-900 rounded-lg p-3 flex items-center justify-between">
                            <div>
                              <div className="text-white">{event.campaign_name}</div>
                              <div className="text-gray-400 text-sm">{event.contacts_remaining} contacts remaining</div>
                            </div>
                            <div className="text-gray-400 text-sm">
                              {new Date(event.timestamp).toLocaleTimeString()}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-gray-400">
                <RefreshCw className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No resume events in the last 30 days
              </div>
            )}
          </div>
        )}

        {/* All Campaigns Tab */}
        {activeTab === 'campaigns' && healthData && (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Campaign</th>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Status</th>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Health</th>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Progress</th>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Failed (24h)</th>
                  <th className="px-4 py-3 text-left text-gray-400 text-sm font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {healthData.campaigns.map(campaign => (
                  <tr key={campaign.campaign_id} className="hover:bg-gray-700/50">
                    <td className="px-4 py-3">
                      <div className="text-white font-medium">{campaign.name}</div>
                      <div className="text-gray-400 text-sm">{campaign.agent_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(campaign.status)}`}>
                        {campaign.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className={`flex items-center gap-2 ${getHealthStatusColor(campaign.health_status)}`}>
                        {getHealthStatusIcon(campaign.health_status)}
                        <span className="capitalize">{campaign.health_status}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500"
                            style={{ width: `${Math.min(100, campaign.progress_percent)}%` }}
                          />
                        </div>
                        <span className="text-gray-400 text-sm">{Math.min(100, campaign.progress_percent)}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {campaign.recent_failures_24h > 0 ? (
                        <span className="text-yellow-400 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          {campaign.recent_failures_24h}
                        </span>
                      ) : (
                        <span className="text-gray-500">0</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => fetchCampaignDetail(campaign.campaign_id)}
                        className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Campaign Detail Modal */}
        {selectedCampaign && campaignDetail && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between sticky top-0 bg-gray-800">
                <div>
                  <h2 className="text-xl font-bold text-white">{campaignDetail.campaign.name}</h2>
                  <p className="text-gray-400 text-sm">{campaignDetail.campaign.agent_name}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedCampaign(null);
                    setCampaignDetail(null);
                  }}
                  className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="text-gray-400 text-sm">Completion Rate</div>
                    <div className="text-2xl font-bold text-white">{campaignDetail.metrics.completion_rate}%</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="text-gray-400 text-sm">Answer Rate</div>
                    <div className="text-2xl font-bold text-green-400">{campaignDetail.metrics.answer_rate}%</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="text-gray-400 text-sm">Failure Rate</div>
                    <div className="text-2xl font-bold text-red-400">{campaignDetail.metrics.failure_rate}%</div>
                  </div>
                  <div className="bg-gray-900 rounded-lg p-4">
                    <div className="text-gray-400 text-sm">Total Contacts</div>
                    <div className="text-2xl font-bold text-white">{campaignDetail.metrics.total_contacts}</div>
                  </div>
                </div>

                {/* Status Breakdown */}
                <div className="bg-gray-900 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3">Contact Status Breakdown</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {Object.entries(campaignDetail.status_breakdown).map(([status, count]) => (
                      <div key={status} className="bg-gray-800 rounded px-3 py-2 flex items-center justify-between">
                        <span className="text-gray-400 capitalize">{status.replace('_', ' ')}</span>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Resume History */}
                {campaignDetail.resume_history.length > 0 && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 text-purple-400" />
                      Resume History ({campaignDetail.total_resumes} total)
                    </h3>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {campaignDetail.resume_history.map((event, idx) => (
                        <div key={`history-${event.timestamp || idx}`} className="bg-gray-800 rounded px-3 py-2 flex items-center justify-between">
                          <span className="text-gray-400">{formatDate(event.timestamp)}</span>
                          <span className="text-purple-400">{event.contacts_remaining} contacts remaining</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Failures */}
                {campaignDetail.recent_failures.length > 0 && (
                  <div className="bg-gray-900 rounded-lg p-4">
                    <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      Recent Failures ({campaignDetail.failure_count_24h} in last 24h)
                    </h3>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {campaignDetail.recent_failures.slice(0, 10).map((failure, idx) => (
                        <div key={`failure-${failure.phone_number}-${failure.attempt_number || idx}-${failure.timestamp || idx}`} className="bg-gray-800 rounded px-3 py-2">
                          <div className="flex items-center justify-between">
                            <span className="text-white font-mono">{failure.phone_number}</span>
                            <span className="text-gray-400 text-sm">Attempt #{failure.attempt_number}</span>
                          </div>
                          <div className="text-red-400 text-sm mt-1 truncate" title={failure.error_message}>
                            {failure.error_message}
                          </div>
                          <div className="text-gray-500 text-xs mt-1">{formatDate(failure.timestamp)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignHealthDashboard;
